'use client'

import React from 'react'
import { FormField, FormItem, FormLabel, FormMessage } from '../ui/form'
import { Control, FieldPath, FieldValues } from 'react-hook-form'
import { InputGroupProps } from './form-input-wrapper'
import { cn } from '@/shared/lib/utils'
import {
  DateRangePickerControl,
  type DateRangePickerValue,
} from './date-range-picker-control'

type FormDateRangeInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  inputGroupProps?: InputGroupProps
  name: TName
  control: Control<TFieldValues>
  disabled?: boolean
}

function FormDateRangeInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ inputGroupProps, name, control, disabled }: FormDateRangeInputProps<TFieldValues, TName>) {
  if (!name) {
    throw new Error('DateInput must have a name')
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const v = field.value as DateRangePickerValue & Record<string, unknown> | undefined
        const rangeValue: DateRangePickerValue = {
          from: v?.from,
          to: v?.to,
        }

        return (
          <FormItem
            className={cn('space-y-1', inputGroupProps?.wrapperClassName)}
          >
            {inputGroupProps?.label ? (
              <FormLabel>{inputGroupProps.label}</FormLabel>
            ) : null}
            <DateRangePickerControl
              value={rangeValue}
              onChange={(next) => {
                const base =
                  field.value && typeof field.value === 'object'
                    ? { ...(field.value as object) }
                    : {}
                field.onChange({ ...base, ...next })
              }}
              error={!!fieldState.error}
              name={String(name)}
              triggerRef={field.ref}
              disabled={disabled}
            />
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

export default FormDateRangeInput
